import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AddRfidInstructions from 'content/modals/AddRfidModal/RfidInstructions';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Dispatch, FC, SetStateAction } from 'react';
import type { DefaultPopupProps } from 'utils/props';

import Modal from 'components/Modal';

import type { RfidId } from 'redux-store/slices/ui/types';

import AddNewRfidContent from './AddNewRfidContent';
import AddRfidGroupSetNameContent from './AddRfidGroupSetNameContent';
import AddRfidGroupSetRfidsContent from './AddRfidGroupSetRfidsContent';
import AddRfidGroupSuccess from './AddRfidGroupSuccess';

export type AddRfidGroupStage =
  | 'name'
  | 'rfids'
  | 'addRfid'
  | 'instructions'
  | 'success';

export interface AddRfidGroupModalState {
  stage: AddRfidGroupStage;
  name: string;
  rfids: RfidId[];
}

export interface AddRfidGroupModalStageProps {
  open: boolean;
  state: AddRfidGroupModalState;
  groupID: number | null;
  setState: Dispatch<SetStateAction<AddRfidGroupModalState>>;
  onClose: () => void;
  onCloseProxy: () => void;
}

const StageComponents: Record<
  AddRfidGroupStage,
  FC<AddRfidGroupModalStageProps>
> = {
  name: AddRfidGroupSetNameContent,
  rfids: AddRfidGroupSetRfidsContent,
  addRfid: AddNewRfidContent,
  instructions: AddRfidInstructions,
  success: AddRfidGroupSuccess,
};

export interface AddRfidGroupModalProps extends DefaultPopupProps {
  preselectedRfids?: RfidId[];
  groupID: number | null;
}

const AddRfidGroupModal: FC<AddRfidGroupModalProps> = ({
  open,
  onClose,
  preselectedRfids,
  groupID,
}) => {
  const [state, setState] = useState<AddRfidGroupModalState>({
    stage: groupID ? 'rfids' : 'name',
    name: '',
    rfids: [],
  });

  useEffect(() => {
    if (open && preselectedRfids) {
      setState(prev => ({
        ...prev,
        rfids: preselectedRfids,
        stage: groupID ? 'rfids' : 'name',
      }));
    }
  }, [groupID, open, preselectedRfids]);

  useEffect(() => {
    if (!open) {
      setState(prev => ({ ...prev, name: '', rfids: [] }));
    }
  }, [open]);

  const StageComponent = useMemo(() => StageComponents[state.stage], [state]);

  const onCloseProxy = useCallback((): void => {
    switch (state.stage) {
      case 'name':
        setState(prev => ({ ...prev, name: '', rfids: [] }));
        onClose();
        break;

      case 'rfids':
        if (groupID) {
          setState(prev => ({ ...prev, name: '', rfids: [] }));
          onClose();
        } else {
          setState(prev => ({ ...prev, stage: 'name', rfids: [] }));
        }
        break;

      case 'addRfid':
        setState(prev => ({ ...prev, stage: 'rfids' }));
        break;

      case 'instructions':
        setState(prev => ({ ...prev, stage: 'addRfid' }));
        break;

      default:
        break;
    }
  }, [groupID, onClose, state.stage]);

  const goBackButtonIcon = useCallback(() => {
    switch (state.stage) {
      case 'addRfid':
      case 'instructions':
        return <ArrowBackRoundedIcon />;
      case 'rfids':
        if (groupID) {
          return undefined;
        } else {
          return <ArrowBackRoundedIcon />;
        }
      default:
        return undefined;
    }
  }, [groupID, state.stage]);

  return (
    <Modal
      open={open}
      onClose={onCloseProxy}
      overrideCloseButtonIcon={goBackButtonIcon()}
      hideCloseButton={state.stage === 'success'}
    >
      <StageComponent
        open={open}
        setState={setState}
        state={state}
        groupID={groupID}
        onClose={onClose}
        onCloseProxy={onCloseProxy}
      />
    </Modal>
  );
};

export default AddRfidGroupModal;
